import { useState, type FC } from 'react'
import { Tabs, type ITabItem, Icon, Typography, Scrollbar } from 'shared/ui'
import { TranscriptContent } from 'shared/ui/AudioPlayer/ui/SectionBottom/TranscriptContent/TranscriptContent'
import { AISummaryContent } from 'shared/ui/AudioPlayer/ui/SectionBottom/AISummaryContent/AISummaryContent'
import type { IResponseRecordTranscriptStatus } from 'shared/ui/AudioPlayer/ui/types'
import {
  type IAudioPlayerSummaryItem,
  type IAudioPlayerTranscriptItem,
} from 'shared/ui/AudioPlayer'
import { TranscriptActions } from 'shared/ui/AudioPlayer/ui/SectionBottom/TranscriptContent/TranscriptActions'
import { AISummaryActions } from 'shared/ui/AudioPlayer/ui/SectionBottom/AISummaryContent/AISummaryActions'
import styles from './styles.module.scss'

type ITranscriptionContentProps = {
  transcriptStatus: IResponseRecordTranscriptStatus
  transcriptions: IAudioPlayerTranscriptItem[]
  summary: IAudioPlayerSummaryItem[]
}

type ITabsKeys = 'ai_summary' | 'transcript'

export const SectionBottom: FC<ITranscriptionContentProps> = ({
  transcriptStatus,
  transcriptions,
  summary,
}) => {
  const [activeTab, setActiveTab] = useState<ITabsKeys>('ai_summary')
  const tabs: ITabItem<ITabsKeys>[] = [
    {
      key: 'ai_summary',
      name: 'AI Summary',
    },
    {
      key: 'transcript',
      name: 'Transcript',
    },
  ]
  if (transcriptStatus === 'failed') {
    return (
      <div className={styles.failed}>
        <Icon
          icon={'alertTriangleSmall'}
          fontSize={16}
          color={'var(--content-destructive-primary)'}
        />
        <Typography variant={'text-input-field'} ariaLabel={'transcript'}>
          Failed to generate transcript
        </Typography>
      </div>
    )
  }

  if (['in_progress', 'init_auto', 'init_manual', 'init_retry'].includes(`${transcriptStatus}`)) {
    return (
      <div className={styles.inProgress}>
        <Icon icon={'stars'} fontSize={16} color={'var(--purple-80)'} />
        <Typography
          variant={'body-rg-regular'}
          className={styles.inProgress__text}
          ariaLabel={'transcript'}
        >
          Generating call transcript & summary...
        </Typography>
      </div>
    )
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.tabs}>
        <Tabs
          tabs={tabs}
          activeTabKey={activeTab}
          handleSelectTab={(tab) => {
            setActiveTab(tab.key)
          }}
          disabledHover={true}
        />

        {activeTab === 'ai_summary' && <AISummaryActions items={summary} />}
        {activeTab === 'transcript' && <TranscriptActions items={transcriptions} />}
      </div>

      <Scrollbar
        className={styles.scroll}
        autoHeightMin={0}
        autoHeightMax={276}
        autoHeight={true}
        style={{
          width: 'calc(100% + 26px)',
        }}
      >
        {activeTab === 'ai_summary' && <AISummaryContent items={summary} />}
        {activeTab === 'transcript' && <TranscriptContent items={transcriptions} />}
      </Scrollbar>
    </div>
  )
}
